import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import UI, { SUPPORTED_LANGUAGES } from "../i18n/strings";

const LanguageContext = createContext(null);

const STORAGE_KEY = "mesc_lang";

function detectInitialLang() {
  const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored && UI[stored]) return stored;
  if (typeof navigator !== "undefined") {
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (UI[nav]) return nav;
  }
  return "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(detectInitialLang);

  useEffect(() => {
    const meta = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
    const dir = meta?.dir || "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const value = useMemo(() => {
    const t = UI[lang] || UI.en;
    return {
      lang,
      setLang,
      t,
      dir: SUPPORTED_LANGUAGES.find((l) => l.code === lang)?.dir || "ltr",
      languages: SUPPORTED_LANGUAGES,
      // Pick a translation block from an article's translations map
      pickArticle: (article) => {
        if (!article || !article.translations) return { title: "", summary: "", content: "" };
        const tr = article.translations[lang]
          || article.translations[article.source_lang]
          || article.translations.en
          || article.translations.ar
          || article.translations.fr
          || {};
        return {
          title: tr.title || "",
          summary: tr.summary || "",
          content: tr.content || "",
        };
      },
    };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
